import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, VStack, HStack, Text, Input, Select, Button, IconButton,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  FormControl, FormLabel, Tooltip, useToast, Spinner, Divider
} from '@chakra-ui/react';
import { FiRefreshCw, FiSave, FiMaximize2, FiFileText } from 'react-icons/fi';
import { api } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const STYLE_SCHEMA = {
  layout: {
    label: 'Layout',
    fields: {
      pageTopMargin: { label: 'Page Top Margin', type: 'text' },
      pageBottomMargin: { label: 'Page Bottom Margin', type: 'text' },
      pageLeftMargin: { label: 'Page Left Margin', type: 'text' },
      pageRightMargin: { label: 'Page Right Margin', type: 'text' },
      contentWidth: { label: 'Content Area Width (px)', type: 'number' },
      tableLeftMargin: { label: 'Table Left Offset (px)', type: 'number' },
      tableWidth: { label: 'Table Width (px)', type: 'number' },
    }
  },
  colors: {
    label: 'Colors',
    fields: {
      bodyText: { label: 'Body Text', type: 'color' },
      bodyBg: { label: 'Body Background', type: 'color' },
      sectionHeaderBg: { label: 'Section Header Background', type: 'color' },
      sectionHeaderText: { label: 'Section Header Text', type: 'color' },
      sectionHeaderBorder: { label: 'Section Header Border', type: 'color' },
      optionalBannerBg: { label: 'Optional Banner Background', type: 'color' },
      optionalBannerText: { label: 'Optional Banner Text', type: 'color' },
      subtotalBorder: { label: 'Subtotal Border', type: 'color' },
      grandTotalBorder: { label: 'Grand Total Border', type: 'color' },
      usageHeaderBg: { label: 'Usage Header Background', type: 'color' },
      usageHeaderText: { label: 'Usage Header Text', type: 'color' },
      usageAverageBg: { label: 'Usage Average Background', type: 'color' },
      usageAverageText: { label: 'Usage Average Text', type: 'color' },
      tableBorder: { label: 'Table Border', type: 'color' },
      signatureBorder: { label: 'Signature Border', type: 'color' },
      overrideOriginalColor: { label: 'Override Original Price Color', type: 'color' },
    }
  },
  typography: {
    label: 'Typography',
    fields: {
      bodyFontFamily: { label: 'Body Font Family', type: 'font' },
      bodyFontSize: { label: 'Body Font Size', type: 'text' },
      tableCellFontSize: { label: 'Table Cell Font Size', type: 'text' },
      tableHeaderFontSize: { label: 'Table Header Font Size', type: 'text' },
      sectionHeaderFontSize: { label: 'Section Header Font Size', type: 'text' },
      grandTotalFontSize: { label: 'Grand Total Font Size', type: 'text' },
      usageHeaderFontSize: { label: 'Usage Header Font Size', type: 'text' },
      usageAverageFontSize: { label: 'Usage Average Font Size', type: 'text' },
      usageItemFontSize: { label: 'Usage Item Font Size', type: 'text' },
      optionalBannerFontSize: { label: 'Optional Banner Font Size', type: 'text' },
      subtotalFontSize: { label: 'Subtotal Font Size', type: 'text' },
      vatNoteFontSize: { label: 'VAT Note Font Size', type: 'text' },
      footerFontSize: { label: 'Footer Font Size', type: 'text' },
    }
  },
  table: {
    label: 'Table',
    fields: {
      colWidthPos: { label: 'Position Column Width', type: 'text' },
      colWidthAmount: { label: 'Amount Column Width', type: 'text' },
      colWidthUnit: { label: 'Unit Column Width', type: 'text' },
      colWidthDescription: { label: 'Description Column Width', type: 'text' },
      colWidthDescriptionNoPricing: { label: 'Description Width (No Pricing)', type: 'text' },
      colWidthUnitPrice: { label: 'Unit Price Column Width', type: 'text' },
      colWidthFinalPrice: { label: 'Final Price Column Width', type: 'text' },
      cellPadding: { label: 'Cell Padding', type: 'text' },
      headerPadding: { label: 'Header Padding', type: 'text' },
      borderWidth: { label: 'Border Width', type: 'text' },
    }
  },
  spacing: {
    label: 'Spacing',
    fields: {
      sectionMarginTop: { label: 'Section Margin Top', type: 'text' },
      subtotalMarginTop: { label: 'Subtotal Margin Top', type: 'text' },
      usageSummaryMarginTop: { label: 'Usage Summary Margin Top', type: 'text' },
      usageListMarginTop: { label: 'Usage List Margin Top', type: 'text' },
      usageAverageMarginTop: { label: 'Usage Average Margin Top', type: 'text' },
      overrideTotalMarginTop: { label: 'Override Total Margin Top', type: 'text' },
      footerMarginTop: { label: 'Footer Margin Top', type: 'text' },
      footerPaddingBottom: { label: 'Footer Padding Bottom', type: 'text' },
      sectionHeaderHeight: { label: 'Section Header Height', type: 'text' },
      sectionHeaderPadding: { label: 'Section Header Padding', type: 'text' },
    }
  },
  footer: {
    label: 'Footer / Signature',
    fields: {
      signatureColumnWidth: { label: 'Signature Column Width', type: 'text' },
      signatureBorderWidth: { label: 'Signature Border Width', type: 'text' },
      signatureBorderMarginBottom: { label: 'Signature Border Margin Bottom', type: 'text' },
      signatureTitleMarginBottom: { label: 'Signature Title Margin Bottom', type: 'text' },
      companyFooterTitleFontSize: { label: 'Company Footer Title Size', type: 'text' },
      companyFooterFontSize: { label: 'Company Footer Text Size', type: 'text' },
      companyFooterFontFamily: { label: 'Company Footer Font', type: 'text' },
      companyFooterLeftMargin: { label: 'Company Footer Left Margin (px)', type: 'number' },
      footerTextAlign: { label: 'Footer Text Alignment', type: 'select', options: ['left', 'center', 'right'] },
      footerBlockMaxWidth: { label: 'Footer Block Max Width', type: 'text' },
    }
  },
};

const PDFStyleEditor = ({ templateId, availableFonts }) => {
  const { t } = useLanguage();
  const toast = useToast();
  const [defaults, setDefaults] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState('');
  const [previewLang, setPreviewLang] = useState('en');
  const debounceRef = useRef(null);
  const externalWindowRef = useRef(null);

  // Load defaults and current template config
  useEffect(() => {
    if (!templateId) return;
    setLoading(true);
    Promise.all([
      api.get('/api/settings/pdf/templates/style-defaults'),
      api.get('/api/settings/pdf/templates'),
      api.get('/api/proposals?limit=20'),
    ]).then(([defaultsRes, templatesRes, proposalsRes]) => {
      const defs = defaultsRes.data?.data?.defaults;
      setDefaults(defs);

      const templates = templatesRes.data?.data?.templates || [];
      const tpl = templates.find(t => String(t.id) === String(templateId));
      if (tpl?.style_config) {
        const cfg = typeof tpl.style_config === 'string' ? JSON.parse(tpl.style_config) : tpl.style_config;
        setOverrides(cfg || {});
      } else {
        setOverrides({});
      }

      setProposals(proposalsRes.data?.data?.proposals || proposalsRes.data?.data || []);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, [templateId]);

  // Debounced preview refresh
  const refreshPreview = useCallback(() => {
    if (!templateId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await api.post(`/api/settings/pdf/templates/${templateId}/preview-html`, {
          style_config: overrides,
          proposal_id: selectedProposal || undefined,
          lang: previewLang,
        });
        const html = res.data?.data?.html || '';
        setPreviewHtml(html);
        // Sync external preview tab if open
        if (externalWindowRef.current && !externalWindowRef.current.closed) {
          externalWindowRef.current.document.open();
          externalWindowRef.current.document.write(html);
          externalWindowRef.current.document.close();
        }
      } catch (e) {
        console.error('Preview error:', e);
      }
      setPreviewLoading(false);
    }, 800);
  }, [templateId, overrides, selectedProposal, previewLang]);

  useEffect(() => {
    if (defaults) refreshPreview();
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [refreshPreview, defaults]);

  const getValue = (category, key) => {
    return overrides[category]?.[key];
  };

  const getDefault = (category, key) => {
    return defaults?.[category]?.[key];
  };

  const isOverridden = (category, key) => {
    return overrides[category]?.[key] !== undefined && overrides[category]?.[key] !== null;
  };

  const setValue = (category, key, value) => {
    setOverrides(prev => {
      const next = { ...prev };
      if (!next[category]) next[category] = {};
      next[category] = { ...next[category], [key]: value };
      return next;
    });
  };

  const resetField = (category, key) => {
    setOverrides(prev => {
      const next = { ...prev };
      if (next[category]) {
        const cat = { ...next[category] };
        delete cat[key];
        if (Object.keys(cat).length === 0) {
          delete next[category];
        } else {
          next[category] = cat;
        }
      }
      return next;
    });
  };

  const resetAll = () => {
    setOverrides({});
  };

  const save = async () => {
    setSaving(true);
    try {
      // Clean overrides: remove empty categories
      const clean = {};
      for (const [cat, vals] of Object.entries(overrides)) {
        if (vals && typeof vals === 'object' && Object.keys(vals).length > 0) {
          clean[cat] = vals;
        }
      }
      await api.put(`/api/settings/pdf/templates/${templateId}`, {
        style_config: Object.keys(clean).length > 0 ? clean : null,
      });
      toast({ title: t('settings.saved') || 'Saved', status: 'success', duration: 2000 });
    } catch (e) {
      toast({ title: t('common.error') || 'Error', status: 'error' });
    }
    setSaving(false);
  };

  const renderField = (category, key, fieldDef) => {
    const currentVal = getValue(category, key);
    const defaultVal = getDefault(category, key);
    const displayVal = currentVal !== undefined && currentVal !== null ? currentVal : '';
    const overridden = isOverridden(category, key);

    return (
      <FormControl key={key}>
        <HStack justify="space-between" align="center" mb={1}>
          <FormLabel mb={0} fontSize="sm" fontWeight={overridden ? 'bold' : 'normal'}>
            {t('settings.pdf_style.field_' + key, fieldDef.label)}
          </FormLabel>
          {overridden && (
            <Tooltip label="Reset to default">
              <IconButton
                icon={<FiRefreshCw />}
                size="xs"
                variant="ghost"
                onClick={() => resetField(category, key)}
                aria-label="Reset"
              />
            </Tooltip>
          )}
        </HStack>
        <HStack>
          {fieldDef.type === 'color' ? (
            <>
              <Input
                type="color"
                value={currentVal || defaultVal || '#000000'}
                onChange={(e) => setValue(category, key, e.target.value)}
                w="60px"
                h="32px"
                p={0}
                border="1px solid"
                borderColor="gray.300"
                cursor="pointer"
              />
              <Input
                size="sm"
                value={displayVal}
                placeholder={String(defaultVal || '')}
                onChange={(e) => setValue(category, key, e.target.value)}
                w="100px"
              />
            </>
          ) : fieldDef.type === 'font' ? (
            <Select
              size="sm"
              value={currentVal || ''}
              onChange={(e) => e.target.value ? setValue(category, key, e.target.value) : resetField(category, key)}
            >
              <option value="">{defaultVal} (default)</option>
              {(availableFonts || []).map(f => (
                <option key={f.name} value={f.name.replace(/-Bold|-Black|-Light|-Regular|-Italic|-SemiBold|-ExtraBold|-ExtraLight|-Medium|-Thin/g, '')}>
                  {f.name.replace(/-Bold|-Black|-Light|-Regular|-Italic|-SemiBold|-ExtraBold|-ExtraLight|-Medium|-Thin/g, '')}
                </option>
              )).filter((v, i, a) => a.findIndex(t => t.key === v.key) === i)}
            </Select>
          ) : fieldDef.type === 'number' ? (
            <Input
              size="sm"
              type="number"
              value={displayVal}
              placeholder={String(defaultVal || '')}
              onChange={(e) => setValue(category, key, e.target.value ? Number(e.target.value) : undefined)}
            />
          ) : fieldDef.type === 'select' ? (
            <Select
              size="sm"
              value={currentVal || ''}
              onChange={(e) => e.target.value ? setValue(category, key, e.target.value) : resetField(category, key)}
            >
              <option value="">{defaultVal} (default)</option>
              {(fieldDef.options || []).map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </Select>
          ) : (
            <Input
              size="sm"
              value={displayVal}
              placeholder={String(defaultVal || '')}
              onChange={(e) => setValue(category, key, e.target.value || undefined)}
            />
          )}
        </HStack>
      </FormControl>
    );
  };

  if (!templateId) {
    return <Text color="gray.500">{t('settings.pdf_style.select_template', 'Select a template to edit styles.')}</Text>;
  }

  if (loading) {
    return <Spinner />;
  }

  return (
    <Box display="flex" gap={6} flexDir={{ base: 'column', lg: 'row' }}>
      {/* Style Form */}
      <Box flex="1" minW="350px" maxW="500px">
        <HStack mb={4} justify="space-between">
          <Text fontWeight="bold" fontSize="lg">{t('settings.pdf_style.title', 'Style Settings')}</Text>
          <HStack>
            <Button size="sm" variant="outline" onClick={resetAll}>
              {t('settings.pdf_style.reset_all', 'Reset All')}
            </Button>
            <Button size="sm" colorScheme="blue" leftIcon={<FiSave />} onClick={save} isLoading={saving}>
              {t('settings.save_button') || 'Save'}
            </Button>
          </HStack>
        </HStack>

        <Accordion allowMultiple defaultIndex={[0]}>
          {Object.entries(STYLE_SCHEMA).map(([catKey, catDef]) => (
            <AccordionItem key={catKey}>
              <AccordionButton>
                <Box flex="1" textAlign="left" fontWeight="semibold">
                  {t('settings.pdf_style.cat_' + catKey, catDef.label)}
                  {overrides[catKey] && Object.keys(overrides[catKey]).length > 0 && (
                    <Text as="span" color="blue.500" fontSize="xs" ml={2}>
                      ({Object.keys(overrides[catKey]).length} override{Object.keys(overrides[catKey]).length > 1 ? 's' : ''})
                    </Text>
                  )}
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel>
                <VStack spacing={3} align="stretch">
                  {Object.entries(catDef.fields).map(([key, fieldDef]) =>
                    renderField(catKey, key, fieldDef)
                  )}
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </Box>

      {/* Preview Panel */}
      <Box flex="2" minW="400px">
        <HStack mb={3} justify="space-between">
          <Text fontWeight="bold" fontSize="lg">{t('settings.pdf_style.preview', 'Preview')}</Text>
          <HStack spacing={2}>
            <Select size="sm" w="200px" value={selectedProposal} onChange={(e) => setSelectedProposal(e.target.value)}>
              <option value="">{t('settings.pdf_style.latest_proposal', 'Latest Proposal')}</option>
              {proposals.map(p => (
                <option key={p.id} value={p.id}>#{p.id} - {p.project || p.subject || 'Proposal'}</option>
              ))}
            </Select>
            <Select size="sm" w="80px" value={previewLang} onChange={(e) => setPreviewLang(e.target.value)}>
              <option value="en">EN</option>
              <option value="de">DE</option>
            </Select>
            <Button size="sm" variant="outline" onClick={refreshPreview} isLoading={previewLoading}>
              {t('settings.pdf_style.refresh', 'Refresh')}
            </Button>
            <Tooltip label="Open HTML in new tab">
              <IconButton
                icon={<FiMaximize2 />}
                size="sm"
                variant="outline"
                aria-label="Full screen preview"
                isDisabled={!previewHtml}
                onClick={() => {
                  if (externalWindowRef.current && !externalWindowRef.current.closed) {
                    externalWindowRef.current.focus();
                  } else {
                    const win = window.open('', '_blank');
                    if (win) {
                      win.document.write(previewHtml);
                      win.document.close();
                      externalWindowRef.current = win;
                    }
                  }
                }}
              />
            </Tooltip>
            <IconButton
                icon={<FiFileText />}
                size="sm"
                variant="solid"
                colorScheme="blue"
                aria-label="Generate real PDF in new tab"
                onClick={async () => {
                  try {
                    const res = await api.post(
                      `/api/settings/pdf/templates/${templateId}/preview-pdf`,
                      { style_config: overrides, proposal_id: selectedProposal || undefined, lang: previewLang },
                      { responseType: 'blob' }
                    );
                    const blob = new Blob([res.data], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    window.open(url + '#zoom=200', '_blank');
                  } catch (e) {
                    toast({ title: t('settings.pdf_style.pdf_failed', 'PDF generation failed'), status: 'error' });
                  }
                }}
              />
          </HStack>
        </HStack>

        <Box
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          bg="white"
          overflow="hidden"
          position="relative"
          minH="600px"
        >
          {previewLoading && (
            <Box position="absolute" top={2} right={2} zIndex={1}>
              <Spinner size="sm" />
            </Box>
          )}
          {previewHtml ? (
            <iframe
              title="PDF Preview"
              srcDoc={previewHtml}
              style={{
                width: '100%',
                height: '800px',
                border: 'none',
                transform: 'scale(0.75)',
                transformOrigin: 'top left',
                width: '133%',
              }}
            />
          ) : (
            <Box p={6} textAlign="center" color="gray.400">
              {t('settings.pdf_style.no_preview', 'No preview available. Make sure there is at least one proposal in the system.')}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PDFStyleEditor;
